import { morph } from "@theme/morph";
import { Component } from "@theme/component";
import { CartUpdateEvent, ThemeEvents } from "@theme/events";
import { DialogComponent, DialogCloseEvent } from "@theme/dialog";
import {
  mediaQueryLarge,
  isMobileBreakpoint,
  getIOSVersion,
} from "@theme/utilities";

export class CtrQuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();

  get productPageUrl() {
    const productCard =
      /** @type {import('./product-card').ProductCard | null} */ (
        this.closest("product-card")
      );
    const productLink = productCard?.getProductCardLink();
    const currentUrl = new URL(window.location.href);

    if (!productLink?.href && !currentUrl.href.includes("products/")) {
      if (!this.dataset.productUrl) return "";
      const url = new URL(currentUrl.origin + this.dataset.productUrl);
      url.searchParams.set("view", "choose-package");
      return url.toString();
    };

    const url = new URL(productLink?.href || currentUrl.href);

    url.searchParams.set("view", "choose-package");
    if (url.searchParams.has("variant")) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set("variant", selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard =
      /** @type {import('./product-card').ProductCard | null} */ (
        this.closest("product-card")
      );
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener("change", this.#closeQuickAddModal);

    // The quick-add drawer content (including the free-shipping progress bar
    // in ctr-expected-delivery.liquid) is fetched from the server and cached
    // per product URL. Cart totals change server-side, so any cached content
    // becomes stale the moment the cart updates — clear it so the next open
    // re-fetches a fresh copy instead of showing an outdated progress bar.
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

    // const dialogComponent = document.getElementById("ctr-quick-add-dialog");
    // if (!(dialogComponent instanceof CtrQuickAddDialog)) return;

    // dialogComponent.addEventListener(DialogCloseEvent.eventName, () =>
    //   this.#closeQuickAddModal()
    // );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener("change", this.#closeQuickAddModal);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.#abortController?.abort();
  }

  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);

      if (html) {
        const packageElement =
          html.querySelector("[data-block-id^='ctr-product-package']");

        if (packageElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (packageElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
    }
    this.#openQuickAddModal();
  };

  /** @param {CtrQuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute("stay-visible", true);

    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => this.toggleAttribute("stay-visible", false),
      {
        once: true,
      }
    );
  }

  #handleSubscriptionChange = (
    /** @type {Event | null} */ event,
    /** @type {HTMLDialogElement} */ DialogElement,
    /** @type {boolean} */ isSubscribed = false
  ) => {
    const newIsSubscribed = event
      ? /** @type {HTMLInputElement} */ (event.target).checked
      : isSubscribed;

    const subscriptionContent = this.querySelector(".js-subscription-content");
    const discountElements = DialogElement.querySelectorAll(
      ".js-subscription-discount"
    );
    const sellingPlanInput = /** @type {HTMLInputElement} */ (
      DialogElement.querySelector("input[name='selling_plan']")
    );
    let sellingPlanId = "";
    const textContent = DialogElement.querySelector(
      `ctr-variant-picker script[type="application/json"]`
    )?.textContent;

    if (sellingPlanInput && textContent) {
      const variant = JSON.parse(textContent);
      sellingPlanId =
        variant?.selling_plan_allocations?.[0]?.selling_plan_id || "";
      sellingPlanInput.value = newIsSubscribed ? sellingPlanId : "";
    }

    discountElements.forEach((element) => {
      element.classList.toggle("hidden", !newIsSubscribed);
    });

    DialogElement.querySelector(".js-subscription-content")?.classList.toggle(
      "hidden",
      !newIsSubscribed
    );

    this.updatePrices(DialogElement, newIsSubscribed);
  };

  updatePrices = (
    /** @type {HTMLDialogElement} */ DialogElement,
    /** @type {boolean} */ newIsSubscribed
  ) => {
    const regularPriceElement = /** @type {HTMLElement} */ (
      DialogElement.querySelector(".js-regular-price")
    );
    const subscriptionPriceElement = /** @type {HTMLElement} */ (
      DialogElement.querySelector(".js-subscription-price")
    );
    const servingPriceElements = /** @type {NodeListOf<HTMLElement>} */ (
      DialogElement.querySelectorAll(".js-serving-price")
    );

    if (servingPriceElements.length > 0) {
      servingPriceElements.forEach((element) => {
        element.innerHTML = newIsSubscribed
          ? `${element.dataset.subscriptionServingPrice}`
          : `${element.dataset.servingPrice}`;
      });
    }

    if (!regularPriceElement || !subscriptionPriceElement) return;

    if (newIsSubscribed) {
      regularPriceElement.classList.add("hidden");
      subscriptionPriceElement.classList.remove("hidden");
      return;
    }

    regularPriceElement.classList.remove("hidden");
    subscriptionPriceElement.classList.add("hidden");
  };

  #handleSubscription = (/** @type {HTMLDialogElement} */ DialogElement) => {
    const subscriptionCheckbox = /** @type {HTMLInputElement} */ (
      DialogElement.querySelector("input[name='subscription']")
    );
    const isSubscribed = subscriptionCheckbox?.checked;
    this.#handleSubscriptionChange(null, DialogElement, isSubscribed);

    subscriptionCheckbox?.addEventListener("change", (event) =>
      this.#handleSubscriptionChange(event, DialogElement)
    );
  };

  #openQuickAddModal = () => {
    const dialogComponent = /** @type {HTMLDialogElement} */ (
      document.getElementById("ctr-quick-add-dialog")
    );
    if (!(dialogComponent instanceof CtrQuickAddDialog)) return;
    this.#stayVisibleUntilDialogCloses(dialogComponent);
    this.#handleSubscription(dialogComponent);
    dialogComponent.showDialog();
    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      this.#handleDialogClose.bind(this, dialogComponent),
      { once: true }
    );
  };

  #handleDialogClose = (
    /** @type {HTMLDialogElement} */ dialogComponent,
    /** @type {DialogCloseEvent} */ event
  ) => {
    const subscriptionCheckbox = /** @type {HTMLInputElement} */ (
      dialogComponent.querySelector("input[name='subscription']")
    );
    subscriptionCheckbox?.removeEventListener(
      "change",
      () => this.#handleSubscriptionChange
    );
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById("ctr-quick-add-dialog");
    if (!(dialogComponent instanceof CtrQuickAddDialog)) return;

    window.dispatchEvent(new CustomEvent("ctrquickaddclosed", {}));
    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch product page: HTTP error ${response.status}`
        );
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, "text/html");

      return html;
    } catch (error) {
      if (error.name === "AbortError") {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById("quick-add-modal-content");
    if (!productGrid || !modalContent) return;

    // if (isMobileBreakpoint()) {
    //   const productDetails = productGrid.querySelector(".product-details");
    //   if (!productDetails) return;
    //   const productFormComponent = productGrid.querySelector(
    //     "product-form-component"
    //   );
    //   const variantPicker =
    //     productGrid.querySelector("ctr-variant-picker") ||
    //     productGrid.querySelector("variant-picker");
    //   const productPrice = productGrid.querySelector("product-price");
    //   const productTitle = document.createElement("a");
    //   productTitle.textContent = this.dataset.productTitle || "";

    //   // Make product title as a link to the product page
    //   productTitle.href = this.productPageUrl;

    //   if (
    //     !productFormComponent ||
    //     !variantPicker ||
    //     !productPrice ||
    //     !productTitle
    //   )
    //     return;

    //   const productHeader = document.createElement("div");
    //   productHeader.classList.add("product-header");

    //   productHeader.appendChild(productTitle);
    //   productHeader.appendChild(productPrice);
    //   productGrid.appendChild(productHeader);
    //   productGrid.appendChild(variantPicker);
    //   productGrid.appendChild(productFormComponent);
    //   productDetails.remove();
    // }
    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll(
      'input[type="radio"][data-variant-id]'
    );
    for (const input of modalInputs) {
      if (
        input instanceof HTMLInputElement &&
        input.dataset.variantId === selectedVariantId &&
        !input.checked
      ) {
        input.checked = true;
        input.dispatchEvent(new Event("change", { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get("ctr-quick-add-component")) {
  customElements.define("ctr-quick-add-component", CtrQuickAddComponent);
}

class CtrQuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(
      ThemeEvents.variantUpdate,
      this.#updateProductTitleLink
    );

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(
      DialogCloseEvent.eventName,
      this.#handleDialogClose
    );
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector(".view-product-title a")
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (
      this.querySelector(".view-product-title a")
    );
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (
      this.querySelector(".product-header a")
    );

    this.#handleSubscriptionChange(event);

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleSubscriptionChange = (/** @type {CustomEvent} */ event) => {
    const subscriptionCheckbox = /** @type {HTMLInputElement} */ (
      this.querySelector("input[name='subscription']")
    );
    const isSubscribed = subscriptionCheckbox?.checked;
    const discountElements = this.querySelectorAll(".js-subscription-discount");
    const sellingPlanInput = /** @type {HTMLInputElement} */ (
      this.querySelector("input[name='selling_plan']")
    );
    const selling_plan_allocations =
      event?.detail?.resource?.selling_plan_allocations || [];

    discountElements.forEach((element) => {
      element.classList.toggle("hidden", !isSubscribed);
    });

    this.updatePrices(this, isSubscribed);

    if (!sellingPlanInput) return;

    if (selling_plan_allocations.length === 0) {
      sellingPlanInput.value = "";
      return;
    }

    sellingPlanInput.value = isSubscribed
      ? selling_plan_allocations[0]?.selling_plan_id
      : "";
  };

  updatePrices = (
    /** @type {CtrQuickAddDialog} */ DialogElement,
    /** @type {boolean} */ newIsSubscribed
  ) => {
    const regularPriceElement = /** @type {HTMLElement} */ (
      DialogElement.querySelector(".js-regular-price")
    );
    const subscriptionPriceElement = /** @type {HTMLElement} */ (
      DialogElement.querySelector(".js-subscription-price")
    );

    const servingPriceElements = /** @type {NodeListOf<HTMLElement>} */ (
      DialogElement.querySelectorAll(".js-serving-price")
    );

    if (servingPriceElements.length > 0) {
      servingPriceElements.forEach((element) => {
        element.innerHTML = newIsSubscribed
          ? `${element.dataset.subscriptionServingPrice}`
          : `${element.dataset.servingPrice}`;
      });
    }

    if (!regularPriceElement || !subscriptionPriceElement) return;

    if (newIsSubscribed) {
      regularPriceElement.classList.add("hidden");
      subscriptionPriceElement.classList.remove("hidden");
      return;
    }

    regularPriceElement.classList.remove("hidden");
    subscriptionPriceElement.classList.add("hidden");
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (
      !iosVersion ||
      iosVersion.major >= 17 ||
      (iosVersion.major === 16 && iosVersion.minor >= 4)
    )
      return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector("#ResultsList [product-grid-view]");
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = "";
        });
      }
    });
  };
}

if (!customElements.get("ctr-quick-add-dialog")) {
  customElements.define("ctr-quick-add-dialog", CtrQuickAddDialog);
}
